<script setup>
import {computed} from 'vue'
import {useViewStore} from '@/stores/view'
import Button from '@/common/Button.vue'
import {getTypeOptions} from "@/utils/filter"

const props = defineProps({
    viewId: {type: String, required: true},
    filterKey: {type: Number, required: true},
    columns: {type: Array, default: () => []}
})

// Dynamic View Store
const viewStore = useViewStore(props.viewId)

const tableName = computed(() => viewStore.tableName || null)
const filter = computed(() => viewStore.filters?.[props.filterKey] || {})

const column = computed(() => {
    if (!props.columns || !filter.value.columnName) return null
    return props.columns.find(o => o.columnName === filter.value.columnName) || null
})

const typeOptions = computed(() => {
    if (!column.value?.type) return []
    return getTypeOptions(column.value.type) || []
})

const setFilter = (name, value) => {
    viewStore.setFilterProp({
        key: props.filterKey,
        name: name,
        value: value
    })
}

// Initial lifecycle logic (created hook equivalent)
const columnType = column.value?.type
if (
    // The IS SET and IS NOT SET conditions also set their values because they do not have fields for storing values.
    // This value must be unset when returning to the advanced filters.
    (
        (filter.value.condition === 'is set' || filter.value.condition === 'is not set')
        && filter.value.value === undefined
    )
    || (
        columnType === 'relationshipOneToMany'
        && (filter.value.condition === 'one is set' || filter.value.condition === 'one is not set')
        && filter.value.value === undefined
    )
    || (
        columnType === 'relationshipManyToMany'
        && (filter.value.condition === 'many is set' || filter.value.condition === 'many is not set')
        && filter.value.value === undefined
    )
) {
    setFilter('condition', '')
}

const setOption = (i) => {
    const option = typeOptions.value[i]
    if (!option) return

    setFilter('condition', option.condition)
    setFilter('predicate', option.predicate)
    setFilter('conditionText', option.text)

    const colType = column.value?.type

    if (
        filter.value.condition === 'is set'
        || filter.value.condition === 'is not set'
    ) {
        setFilter('value', filter.value.condition)

    } else if (colType === 'relationshipOneToMany') {
        if (option.condition === 'one is set' || option.condition === 'one is not set') {
            setFilter('value', 0)
        } else if (filter.value.value === 0) { // Retain the selected item when changing between IS and IS NOT
            setFilter('value', undefined)
        }

    } else if (colType === 'relationshipManyToMany') {
        if (option.condition === 'many is set') {
            setFilter('value', 'IS SET')
        } else if (option.condition === 'many is not set') {
            setFilter('value', 'IS NOT SET')
        }
    }
}
</script>

<template>
    <div class="dropdown">
        <span v-if="typeOptions.length === 1" class="text-nowrap small">
            {{ filter.conditionText }}
        </span>

        <Button v-if="typeOptions.length > 1" data-bs-toggle="dropdown" class="btn-sm border text-nowrap focus-ring">
            {{ filter.conditionText }}
        </Button>

        <ul class="dropdown-menu" role="menu">
            <li v-for="(option, index) in typeOptions" :key="option.id || index" @click="setOption(index)">
                <a href @click.prevent :class="{'active': option.text === filter.conditionText}"
                   class="dropdown-item">{{ option.text }}</a>
            </li>
        </ul>
    </div>
</template>